/**
 * Demo Data Service
 *
 * Centralized service for managing and retrieving demo data.
 * Caches data in sessionStorage for consistency across demo pages.
 */

import { NexusAgent, NexusConversation, CRMLead, CRMContact, CRMCompany, CRMOpportunity, CRMActivity } from "@/types/nexus"
import { NexusBooking, BookingService, BookingResource } from "@/types/booking"
import { 
  generateMockAgents, 
  generateMockConversations, 
  generateMockAnalytics, 
  generateMockIntegrations,
  generateMockBookingServices,
  generateMockBookingResources,
  generateMockBookings,
  generateMockCRMLeads,
  generateMockCRMContacts,
  generateMockCRMCompanies,
  generateMockCRMOpportunities,
  generateMockCRMActivities
} from "./mock-data-generator"
import { createTranscriptFromMessages } from "@/lib/nexus/transcript-utils"

const STORAGE_KEY_AGENTS = 'nexus_demo_agents'
const STORAGE_KEY_CONVERSATIONS = 'nexus_demo_conversations'
const STORAGE_KEY_ANALYTICS = 'nexus_demo_analytics'
const STORAGE_KEY_INTEGRATIONS = 'nexus_demo_integrations'
const STORAGE_KEY_BOOKINGS = 'nexus_demo_bookings'
const STORAGE_KEY_BOOKING_SERVICES = 'nexus_demo_booking_services'
const STORAGE_KEY_BOOKING_RESOURCES = 'nexus_demo_booking_resources'
const STORAGE_KEY_CRM_LEADS = 'nexus_demo_crm_leads'
const STORAGE_KEY_CRM_CONTACTS = 'nexus_demo_crm_contacts'
const STORAGE_KEY_CRM_COMPANIES = 'nexus_demo_crm_companies'
const STORAGE_KEY_CRM_OPPORTUNITIES = 'nexus_demo_crm_opportunities'
const STORAGE_KEY_CRM_ACTIVITIES = 'nexus_demo_crm_activities'

class DemoDataService {
  private agents: NexusAgent[] | null = null
  private conversations: NexusConversation[] | null = null
  private analytics: any = null
  private integrations: any = null
  private bookings: NexusBooking[] | null = null
  private bookingServices: BookingService[] | null = null
  private bookingResources: BookingResource[] | null = null
  private crmLeads: CRMLead[] | null = null
  private crmContacts: CRMContact[] | null = null
  private crmCompanies: CRMCompany[] | null = null
  private crmOpportunities: CRMOpportunity[] | null = null
  private crmActivities: CRMActivity[] | null = null

  // Initialize demo data (generate if not cached)
  initialize() {
    if (typeof window === 'undefined') return

    // Load from sessionStorage or generate new
    const cachedAgents = sessionStorage.getItem(STORAGE_KEY_AGENTS)
    if (cachedAgents) {
      this.agents = JSON.parse(cachedAgents)
    } else {
      this.agents = generateMockAgents()
      sessionStorage.setItem(STORAGE_KEY_AGENTS, JSON.stringify(this.agents))
    }

    const cachedConversations = sessionStorage.getItem(STORAGE_KEY_CONVERSATIONS)
    if (cachedConversations) {
      this.conversations = JSON.parse(cachedConversations)
    } else {
      const agentIds = (this.agents || []).map(a => a.id)
      this.conversations = generateMockConversations(agentIds)
      sessionStorage.setItem(STORAGE_KEY_CONVERSATIONS, JSON.stringify(this.conversations))
    }

    const cachedAnalytics = sessionStorage.getItem(STORAGE_KEY_ANALYTICS)
    if (cachedAnalytics) {
      this.analytics = JSON.parse(cachedAnalytics)
    } else {
      this.analytics = generateMockAnalytics()
      sessionStorage.setItem(STORAGE_KEY_ANALYTICS, JSON.stringify(this.analytics))
    }

    const cachedIntegrations = sessionStorage.getItem(STORAGE_KEY_INTEGRATIONS)
    if (cachedIntegrations) {
      this.integrations = JSON.parse(cachedIntegrations)
    } else {
      this.integrations = generateMockIntegrations()
      sessionStorage.setItem(STORAGE_KEY_INTEGRATIONS, JSON.stringify(this.integrations))
    }

    // Initialize booking data
    const cachedBookingServices = sessionStorage.getItem(STORAGE_KEY_BOOKING_SERVICES)
    if (cachedBookingServices) {
      this.bookingServices = JSON.parse(cachedBookingServices)
    } else {
      this.bookingServices = generateMockBookingServices()
      sessionStorage.setItem(STORAGE_KEY_BOOKING_SERVICES, JSON.stringify(this.bookingServices))
    }

    const cachedBookingResources = sessionStorage.getItem(STORAGE_KEY_BOOKING_RESOURCES)
    if (cachedBookingResources) {
      this.bookingResources = JSON.parse(cachedBookingResources)
    } else {
      this.bookingResources = generateMockBookingResources()
      sessionStorage.setItem(STORAGE_KEY_BOOKING_RESOURCES, JSON.stringify(this.bookingResources))
    }

    const cachedBookings = sessionStorage.getItem(STORAGE_KEY_BOOKINGS)
    if (cachedBookings) {
      this.bookings = JSON.parse(cachedBookings)
    } else {
      const services = this.bookingServices || generateMockBookingServices()
      const resources = this.bookingResources || generateMockBookingResources()
      this.bookings = generateMockBookings(
        services.map(s => s.id),
        resources.map(r => r.id)
      )
      sessionStorage.setItem(STORAGE_KEY_BOOKINGS, JSON.stringify(this.bookings))
    }

    // Initialize CRM data
    const cachedCRMLeads = sessionStorage.getItem(STORAGE_KEY_CRM_LEADS)
    if (cachedCRMLeads) {
      this.crmLeads = JSON.parse(cachedCRMLeads)
    } else {
      this.crmLeads = generateMockCRMLeads()
      sessionStorage.setItem(STORAGE_KEY_CRM_LEADS, JSON.stringify(this.crmLeads))
    }

    const cachedCRMContacts = sessionStorage.getItem(STORAGE_KEY_CRM_CONTACTS)
    if (cachedCRMContacts) {
      this.crmContacts = JSON.parse(cachedCRMContacts)
    } else {
      this.crmContacts = generateMockCRMContacts()
      sessionStorage.setItem(STORAGE_KEY_CRM_CONTACTS, JSON.stringify(this.crmContacts))
    }

    const cachedCRMCompanies = sessionStorage.getItem(STORAGE_KEY_CRM_COMPANIES)
    if (cachedCRMCompanies) {
      this.crmCompanies = JSON.parse(cachedCRMCompanies)
    } else {
      this.crmCompanies = generateMockCRMCompanies()
      sessionStorage.setItem(STORAGE_KEY_CRM_COMPANIES, JSON.stringify(this.crmCompanies))
    }

    const cachedCRMOpportunities = sessionStorage.getItem(STORAGE_KEY_CRM_OPPORTUNITIES)
    if (cachedCRMOpportunities) {
      this.crmOpportunities = JSON.parse(cachedCRMOpportunities)
    } else {
      const leads = this.crmLeads || generateMockCRMLeads()
      const contacts = this.crmContacts || generateMockCRMContacts()
      const companies = this.crmCompanies || generateMockCRMCompanies()
      this.crmOpportunities = generateMockCRMOpportunities(
        leads.map(l => l.id),
        contacts.map(c => c.id),
        companies.map(c => c.id)
      )
      sessionStorage.setItem(STORAGE_KEY_CRM_OPPORTUNITIES, JSON.stringify(this.crmOpportunities))
    }

    const cachedCRMActivities = sessionStorage.getItem(STORAGE_KEY_CRM_ACTIVITIES)
    if (cachedCRMActivities) {
      this.crmActivities = JSON.parse(cachedCRMActivities)
    } else {
      const leads = this.crmLeads || generateMockCRMLeads()
      const contacts = this.crmContacts || generateMockCRMContacts()
      this.crmActivities = generateMockCRMActivities(
        contacts.map(c => c.id),
        leads.map(l => l.id)
      )
      sessionStorage.setItem(STORAGE_KEY_CRM_ACTIVITIES, JSON.stringify(this.crmActivities))
    }
  }

  // Get all agents (server: use mock data; client: initialize from sessionStorage)
  getAgents(): NexusAgent[] {
    if (!this.agents) {
      this.initialize()
      // Server has no sessionStorage; generate mock agents so demo API works
      if (typeof window === "undefined" && !this.agents) {
        this.agents = generateMockAgents()
      }
    }
    return this.agents || []
  }

  // Get agent by ID
  getAgentById(agentId: string): NexusAgent | null {
    const agents = this.getAgents()
    return agents.find(a => a.id === agentId) || null
  }

  // Get conversations
  getConversations(limit?: number): NexusConversation[] {
    if (!this.conversations) this.initialize()
    const conversations = this.conversations || []
    return limit ? conversations.slice(0, limit) : conversations
  }

  // Get conversation by ID
  getConversationById(conversationId: string): NexusConversation | null {
    const conversations = this.getConversations()
    return conversations.find(c => c.id === conversationId) || null
  }

  // Get analytics
  getAnalytics() {
    if (!this.analytics) this.initialize()
    return this.analytics || generateMockAnalytics()
  }

  // Get integrations
  getIntegrations() {
    if (!this.integrations) this.initialize()
    return this.integrations || []
  }

  // Get bookings
  getBookings(): NexusBooking[] {
    if (!this.bookings) this.initialize()
    return this.bookings || []
  }

  // Get booking by ID
  getBookingById(bookingId: string): NexusBooking | null {
    const bookings = this.getBookings()
    return bookings.find(b => b.id === bookingId) || null
  }

  // Get booking services
  getBookingServices(): BookingService[] {
    if (!this.bookingServices) this.initialize()
    return this.bookingServices || []
  }

  // Get booking service by ID
  getBookingServiceById(serviceId: string): BookingService | null {
    const services = this.getBookingServices()
    return services.find(s => s.id === serviceId) || null
  }

  // Get booking resources
  getBookingResources(): BookingResource[] {
    if (!this.bookingResources) this.initialize()
    return this.bookingResources || []
  }

  // Get booking resource by ID
  getBookingResourceById(resourceId: string): BookingResource | null {
    const resources = this.getBookingResources()
    return resources.find(r => r.id === resourceId) || null
  }

  // Get CRM leads
  getCRMLeads(): CRMLead[] {
    if (!this.crmLeads) this.initialize()
    return this.crmLeads || []
  }

  // Get CRM lead by ID
  getCRMLeadById(leadId: string): CRMLead | null {
    const leads = this.getCRMLeads()
    return leads.find(l => l.id === leadId) || null
  }

  // Get CRM contacts
  getCRMContacts(): CRMContact[] {
    if (!this.crmContacts) this.initialize()
    return this.crmContacts || []
  }

  // Get CRM contact by ID
  getCRMContactById(contactId: string): CRMContact | null {
    const contacts = this.getCRMContacts()
    return contacts.find(c => c.id === contactId) || null
  }

  // Get CRM companies
  getCRMCompanies(): CRMCompany[] {
    if (!this.crmCompanies) this.initialize()
    return this.crmCompanies || []
  }

  // Get CRM company by ID
  getCRMCompanyById(companyId: string): CRMCompany | null {
    const companies = this.getCRMCompanies()
    return companies.find(c => c.id === companyId) || null
  }

  // Get CRM opportunities
  getCRMOpportunities(): CRMOpportunity[] {
    if (!this.crmOpportunities) this.initialize()
    return this.crmOpportunities || []
  }

  // Get CRM activities
  getCRMActivities(): CRMActivity[] {
    if (!this.crmActivities) this.initialize()
    return this.crmActivities || []
  }

  // Create demo call (simulated outbound call)
  createDemoCall(contactId: string, phoneNumber: string, customPrompt?: string): NexusConversation {
    const agents = this.getAgents()
    const voiceAgent = agents.find(a => a.type === 'voice' || a.type === 'hybrid') || agents[0]
    const contact = this.getCRMContactById(contactId)
    const lead = this.getCRMLeads().find(l => l.phone === phoneNumber)

    const now = new Date()
    const duration = Math.floor(Math.random() * 300 + 60) // 1-6 minutes
    const endedAt = new Date(now.getTime() + duration * 1000)

    const transcriptMessages = [
      { role: 'assistant', content: customPrompt ? `Hello, this is an AI assistant calling. ${customPrompt}` : 'Hello, this is an AI assistant calling. How can I help you today?' },
      { role: 'user', content: 'Hi, yes I\'m interested' },
      { role: 'assistant', content: 'Great! I\'d be happy to help. What would you like to know more about?' },
      { role: 'user', content: 'Tell me about your services' },
      { role: 'assistant', content: 'We offer comprehensive AI solutions for customer engagement. Would you like to schedule a demo?' },
      { role: 'user', content: 'Yes, that sounds good' },
      { role: 'assistant', content: 'Perfect! I\'ll send you a calendar invite with available times. Is there anything else I can help with?' },
      { role: 'user', content: 'No, that\'s all. Thank you!' },
      { role: 'assistant', content: 'You\'re welcome! Have a great day!' }
    ]
    const transcriptStr = createTranscriptFromMessages(
      transcriptMessages.map((m) => ({ speaker: m.role as "user" | "assistant", content: m.content }))
    )

    const conversation: NexusConversation = {
      id: `demo-conversation-${Date.now()}`,
      agent_id: voiceAgent.id,
      conversation_type: 'voice',
      participant_info: {
        name: contact ? `${contact.first_name} ${contact.last_name}` : lead ? `${lead.first_name} ${lead.last_name}` : 'Unknown',
        phone: phoneNumber,
        email: contact?.email || lead?.email
      },
      started_at: now.toISOString(),
      ended_at: endedAt.toISOString(),
      duration_seconds: duration,
      transcript: transcriptStr,
      summary: `Outbound call to ${phoneNumber}. Discussed services and scheduled demo.`
    }

    // Add to conversations list
    const conversations = this.getConversations()
    conversations.unshift(conversation)
    this.conversations = conversations
    sessionStorage.setItem(STORAGE_KEY_CONVERSATIONS, JSON.stringify(conversations))

    return conversation
  }

  // Clear all demo data (for reset)
  clear() {
    if (typeof window === 'undefined') return
    sessionStorage.removeItem(STORAGE_KEY_AGENTS)
    sessionStorage.removeItem(STORAGE_KEY_CONVERSATIONS)
    sessionStorage.removeItem(STORAGE_KEY_ANALYTICS)
    sessionStorage.removeItem(STORAGE_KEY_INTEGRATIONS)
    sessionStorage.removeItem(STORAGE_KEY_BOOKINGS)
    sessionStorage.removeItem(STORAGE_KEY_BOOKING_SERVICES)
    sessionStorage.removeItem(STORAGE_KEY_BOOKING_RESOURCES)
    sessionStorage.removeItem(STORAGE_KEY_CRM_LEADS)
    sessionStorage.removeItem(STORAGE_KEY_CRM_CONTACTS)
    sessionStorage.removeItem(STORAGE_KEY_CRM_COMPANIES)
    sessionStorage.removeItem(STORAGE_KEY_CRM_OPPORTUNITIES)
    sessionStorage.removeItem(STORAGE_KEY_CRM_ACTIVITIES)
    this.agents = null
    this.conversations = null
    this.analytics = null
    this.integrations = null
    this.bookings = null
    this.bookingServices = null
    this.bookingResources = null
    this.crmLeads = null
    this.crmContacts = null
    this.crmCompanies = null
    this.crmOpportunities = null
    this.crmActivities = null
  }

  // Regenerate all data
  regenerate() {
    this.clear()
    this.initialize()
  }
}

// Singleton instance
export const demoDataService = new DemoDataService()
